import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemeMode = 'system' | 'dark' | 'light';
export type ExperienceLevel = 'guided' | 'professional';

interface SettingsState {
  themeMode: ThemeMode;
  experienceLevel: ExperienceLevel;
  reducedMotion: boolean;
  autosaveEnabled: boolean;
  setThemeMode: (mode: ThemeMode) => void;
  setExperienceLevel: (level: ExperienceLevel) => void;
  toggleReducedMotion: () => void;
  setAutosaveEnabled: (enabled: boolean) => void;
}

/**
 * Seed the reduced-motion preference from the OS the first time the app runs,
 * so someone who has already asked their system for less motion does not have
 * to ask this app separately. Persisted state wins on every later load.
 */
function systemPrefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

/**
 * User preferences that should survive a reload. Kept separate from `uiStore`
 * (ephemeral: palette/notifications) and `layoutStore` (panel arrangement) so
 * that each persisted blob stays small and independently resettable.
 */
export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      themeMode: 'system',
      experienceLevel: 'guided',
      reducedMotion: systemPrefersReducedMotion(),
      autosaveEnabled: true,
      setThemeMode: (themeMode) => set({ themeMode }),
      setExperienceLevel: (experienceLevel) => set({ experienceLevel }),
      toggleReducedMotion: () => set((state) => ({ reducedMotion: !state.reducedMotion })),
      setAutosaveEnabled: (autosaveEnabled) => set({ autosaveEnabled }),
    }),
    { name: 'trajectory.settings' },
  ),
);
